import { normaliseGrade } from "@vavr/core";
// CSV for the guided roster import (plan §7, D9): an RFC 4180 parser, header auto-mapping for
// Procare/Brightwheel-style exports, date normalisation, and the rows the import API takes.
// Pure functions: parsed in the browser, nothing leaves until the dry run.

/** Picks the delimiter from the first line: comma unless tabs or semicolons clearly win. */
export function detectDelimiter(text: string): string {
  const first = text.replace(/^﻿/, "").split(/\r\n|\n|\r/, 1)[0] ?? "";
  let inQ = false;
  const counts: Record<string, number> = { ",": 0, "\t": 0, ";": 0 };
  for (const ch of first) {
    if (ch === '"') inQ = !inQ;
    else if (!inQ && ch in counts) counts[ch]!++;
  }
  if (counts["\t"]! > counts[","]! && counts["\t"]! >= counts[";"]!) return "\t";
  if (counts[";"]! > counts[","]!) return ";";
  return ",";
}

/**
 * RFC 4180: fields separated by the delimiter, records by CRLF (LF and CR alone are accepted),
 * double-quoted fields may hold delimiters, line breaks and "" for a quote. A leading UTF-8 BOM
 * is dropped. Blank lines are skipped. A quote inside an unquoted field is kept as text.
 */
export function parseCsv(input: string, delimiter = detectDelimiter(input)): string[][] {
  const text = input.replace(/^﻿/, "");
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false; // inside a quoted field
  let wasQuoted = false; // this field started with a quote
  const endField = () => { record.push(field); field = ""; wasQuoted = false; };
  const endRecord = () => {
    const blank = record.length === 0 && field === "" && !wasQuoted;
    endField();
    if (!blank) records.push(record);
    record = [];
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"' && field === "" && !wasQuoted) { quoted = true; wasQuoted = true; continue; }
    if (ch === delimiter) { endField(); continue; }
    if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      endRecord();
      continue;
    }
    field += ch;
  }
  if (field !== "" || record.length > 0 || wasQuoted) endRecord();
  return records;
}

// ---------- mapping ----------

export const TARGETS = [
  { key: "child_first", label: "Child first name" },
  { key: "child_last", label: "Child last name" },
  { key: "child_name", label: "Child full name" },
  { key: "child_dob", label: "Date of birth" },
  { key: "room", label: "Room or class" },
  { key: "grade", label: "Grade" },
  { key: "external_ref", label: "Child ID (from your old system)" },
  ...[1, 2, 3].flatMap((n) => [
    { key: `adult${n}_first`, label: `Parent ${n} first name` },
    { key: `adult${n}_last`, label: `Parent ${n} last name` },
    { key: `adult${n}_name`, label: `Parent ${n} full name` },
    { key: `adult${n}_email`, label: `Parent ${n} email` },
    { key: `adult${n}_phone`, label: `Parent ${n} phone` },
    { key: `adult${n}_relation`, label: `Parent ${n} relationship` },
  ]),
] as const;
export type TargetKey = (typeof TARGETS)[number]["key"];
export type Mapping = (TargetKey | null)[];

const words = (h: string) => h.toLowerCase().replace(/['’]s\b/g, "").replace(/([a-z])(\d)/g, "$1 $2").replace(/[^a-z0-9]+/g, " ").trim();
const ADULT = /\b(parent|guardian|contact|mother|father|mom|dad|adult|caregiver|family|pickup)\b/;

function fieldOf(w: string, adult: boolean): string | null {
  if (/\b(e ?mail)\b/.test(w)) return "email";
  if (/\b(phone|mobile|cell|telephone|tel)\b/.test(w)) return "phone";
  if (/\b(relation|relationship)\b/.test(w)) return adult ? "relation" : null;
  if (/\b(dob|birth|birthday|birthdate)\b/.test(w)) return adult ? null : "dob";
  if (/\b(grade|grade level|year group|year level)\b/.test(w)) return adult ? null : "grade";
  if (/\b(room|classroom|class|group|homeroom|program)\b/.test(w)) return adult ? null : "room";
  if (/\b(first|given|fname|firstname|forename)\b/.test(w)) return "first";
  if (/\b(last|surname|family name|lname|lastname)\b/.test(w)) return "last";
  if (/\b(name|full name|fullname)\b/.test(w)) return "name";
  if (!adult && /\b(id|number|ref|reference)\b/.test(w)) return "id";
  return null;
}

/** Guesses a target for each header; each target is used at most once, first come. */
export function autoMap(headers: string[]): Mapping {
  const used = new Set<string>();
  const counters = new Map<string, number>(); // "mother"/"father"/unnumbered parent columns
  return headers.map((h) => {
    const w = words(h).replace(/\bfamily name\b/, "surname");
    if (!w) return null;
    const adultWord = w.match(ADULT)?.[1];
    const adult = !!adultWord && !/\b(child|student|kid)\b/.test(w);
    const field = fieldOf(w, adult);
    if (!field) return null;
    let key: string;
    if (adult) {
      const num = w.match(new RegExp(`\\b${adultWord}\\s*(\\d)\\b`))?.[1] ?? w.match(/\b(\d)\b/)?.[1];
      let n = num ? Number(num) : /\b(secondary|second|alternate)\b/.test(w) ? 2 : adultWord === "father" || adultWord === "dad" ? 2 : 1;
      if (!num && !/\b(primary|secondary|second|alternate|mother|mom|father|dad)\b/.test(w)) n = counters.get(field) ?? 1;
      if (n < 1 || n > 3) return null;
      key = `adult${n}_${field}`;
      if (!num) counters.set(field, n + 1);
    } else {
      key = field === "id" ? "external_ref" : field === "room" ? "room" : field === "grade" ? "grade" : `child_${field}`;
    }
    if (used.has(key)) return null;
    used.add(key);
    return key as TargetKey;
  });
}

// ---------- values ----------

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n: number) => String(n).padStart(2, "0");

function ymd(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1) return null;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d > days || y < 1900 || y > 2100) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/**
 * A date of birth in any of the common export formats as YYYY-MM-DD, or null. US order for
 * slashes (M/D/YYYY). Two-digit years at or below `pivotYear` % 100 are 20xx, else 19xx.
 */
export function normaliseDate(raw: string, pivotYear = new Date().getFullYear()): string | null {
  const s = raw.trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T ].*)?$/);
  if (m) return ymd(+m[1]!, +m[2]!, +m[3]!);
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?: .*)?$/);
  if (m) {
    let y = +m[3]!;
    if (m[3]!.length === 2) y += y <= pivotYear % 100 ? 2000 : 1900;
    return ymd(y, +m[1]!, +m[2]!);
  }
  m = s.match(/^(\d{1,2})[- ]([A-Za-z]{3,})\.?[- ,]+(\d{4})$/);
  if (m) { const mo = MONTHS.indexOf(m[2]!.slice(0, 3).toLowerCase()); return mo < 0 ? null : ymd(+m[3]!, mo + 1, +m[1]!); }
  m = s.match(/^([A-Za-z]{3,})\.? (\d{1,2})(?:st|nd|rd|th)?,? (\d{4})$/);
  if (m) { const mo = MONTHS.indexOf(m[1]!.slice(0, 3).toLowerCase()); return mo < 0 ? null : ymd(+m[3]!, mo + 1, +m[2]!); }
  // Spreadsheet serial day (days since 1899-12-30), as some exports write dates.
  if (/^\d{5}$/.test(s) && +s > 20000 && +s < 80000) {
    const d = new Date(Date.UTC(1899, 11, 30) + +s * 86_400_000);
    return ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }
  return null;
}

/** "Lopez, Ana" or "Ana Maria Lopez" → first and last. */
export function splitName(full: string): { first: string; last: string } {
  const s = full.trim().replace(/\s+/g, " ");
  if (s.includes(",")) {
    const [last, first] = s.split(",", 2).map((x) => x.trim());
    return { first: first ?? "", last: last ?? "" };
  }
  const i = s.lastIndexOf(" ");
  return i < 0 ? { first: s, last: "" } : { first: s.slice(0, i), last: s.slice(i + 1) };
}

export interface BuiltRow { source: number; row: Record<string, string>; problems: string[] }

/**
 * Applies the mapping to data records (header excluded) and returns rows for the import API,
 * with problems the browser can already see. `source` is the 0-based data row index.
 */
export function buildRows(records: string[][], mapping: Mapping, pivotYear?: number): BuiltRow[] {
  const out: BuiltRow[] = [];
  records.forEach((rec, source) => {
    if (rec.every((v) => !v.trim())) return;
    const v: Record<string, string> = {};
    mapping.forEach((key, col) => { const val = rec[col]?.trim(); if (key && val) v[key] = val; });
    const row: Record<string, string> = {};
    const problems: string[] = [];
    const nameInto = (prefix: string) => {
      const full = v[`${prefix}_name`];
      const first = v[`${prefix}_first`] ?? (full ? splitName(full).first : undefined);
      const last = v[`${prefix}_last`] ?? (full ? splitName(full).last : undefined);
      if (first) row[`${prefix}_first`] = first;
      if (last) row[`${prefix}_last`] = last;
    };
    nameInto("child");
    if (!row.child_first) problems.push("Child first name is missing");
    if (!row.child_last) problems.push("Child last name is missing");
    if (v.child_dob) {
      const d = normaliseDate(v.child_dob, pivotYear);
      if (d) row.child_dob = d; else { row.child_dob = v.child_dob; problems.push(`Date of birth "${v.child_dob}" is not a date we recognise`); }
    }
    if (v.room) row.room = v.room;
    if (v.grade) {
      const g = normaliseGrade(v.grade);
      if (g) row.grade = g; else problems.push(`Grade "${v.grade}" is not a grade we recognise (Pre-K, K, 1 to 12)`);
    }
    if (v.external_ref) row.external_ref = v.external_ref;
    for (const n of [1, 2, 3]) {
      const p = `adult${n}`;
      nameInto(p);
      for (const f of ["email", "phone", "relation"]) if (v[`${p}_${f}`]) row[`${p}_${f}`] = v[`${p}_${f}`]!;
      const any = row[`${p}_first`] || row[`${p}_last`] || row[`${p}_email`] || row[`${p}_phone`];
      if (any && (!row[`${p}_first`] || !row[`${p}_last`])) problems.push(`Parent ${n} needs a first and last name to be added`);
      if (row[`${p}_email`] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row[`${p}_email`]!)) problems.push(`Parent ${n} email looks wrong`);
    }
    out.push({ source, row, problems });
  });
  return out;
}

/** The server's duplicate key: first, last and date of birth, case-insensitive. */
export const childKey = (first: string, last: string, dob: string | null | undefined) =>
  `${first.trim().toLowerCase()}|${last.trim().toLowerCase()}|${dob ?? ""}`;
